import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { INQUIRY_STATUSES, BOARD_OPTIONS } from '../../data/indianStates';
import { canAssignCrmInquiries, canDeleteCrmInquiries } from '../../utils/crmPermissions';
import CrmConvertJoinDialog from './CrmConvertJoinDialog';

function employeeLabel(emp) {
  const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  return name ? `${name} (${emp.employee_code})` : emp.employee_code;
}

function statusLabel(value) {
  return INQUIRY_STATUSES.find((s) => s.value === value)?.label || value;
}

function boardLabel(value) {
  return BOARD_OPTIONS.find((b) => b.value === value)?.label || value || '—';
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="font-medium">{value || '—'}</p>
    </div>
  );
}

export default function CrmInquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const canAssign = canAssignCrmInquiries(user);
  const canDelete = canDeleteCrmInquiries(user);
  const [inquiry, setInquiry] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  const fetchInquiry = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/crm/inquiries/${id}`);
      setInquiry(res.data?.data || null);
    } catch (e) {
      setInquiry(null);
      toast({ title: 'Error', description: e.response?.data?.message || 'Inquiry not found', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    if (!canAssign) return;
    try {
      const res = await api.get('/crm/employees');
      setEmployees((res.data?.data?.items || []).filter((e) => e.status === 'active'));
    } catch {
      setEmployees([]);
    }
  };

  useEffect(() => {
    fetchInquiry();
    fetchEmployees();
  }, [id, canAssign]);

  const handleAssign = async (value) => {
    const employeeId = value === 'unassigned' ? null : Number(value);
    setAssigning(true);
    try {
      const res = await api.put(`/crm/inquiries/${id}`, { employee_id: employeeId });
      setInquiry(res.data?.data || null);
      toast({ title: 'Assignment updated' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setAssigning(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/crm/inquiries/${id}`);
      toast({ title: 'Moved to Bin', description: 'Inquiry can be restored from Bin.' });
      navigate('/crm/inquiries');
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  if (loading) return <p className="p-6">Loading…</p>;
  if (!inquiry) return <p className="p-6">Inquiry not found</p>;

  return (
    <div className="space-y-6" data-testid="crm-inquiry-detail">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <Link to="/crm/inquiries" className="text-sm text-blue-600 hover:underline">← Back to inquiries</Link>
          <h1 className="text-2xl font-bold mt-2">{inquiry.school_name}</h1>
          <p className="text-muted-foreground">{statusLabel(inquiry.status)}</p>
          {inquiry.tenant_school_id && (
            <Link
              to={`/schools/${inquiry.tenant_school_id}`}
              className="text-sm text-blue-600 hover:underline mt-1 inline-block"
            >
              View school account →
            </Link>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/crm/inquiries', { state: { editId: inquiry.id } })}>
            Edit
          </Button>
          {!inquiry.tenant_school_id && (
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => setConvertOpen(true)}>
              Convert to Join
            </Button>
          )}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">Delete</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete inquiry?</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={remove}
                    className="bg-destructive text-destructive-foreground"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Assignment &amp; Creator</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Assigned To</Label>
            {canAssign ? (
              <Select
                value={inquiry.employee_id ? String(inquiry.employee_id) : 'unassigned'}
                onValueChange={handleAssign}
                disabled={assigning}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={String(emp.id)}>{employeeLabel(emp)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="font-medium mt-1">{inquiry.assigned_to_name || 'Unassigned'}</p>
            )}
          </div>
          <div>
            <Label>Inquiry Added By</Label>
            <p className="font-medium mt-1">{inquiry.created_by_label || 'Unknown'}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>School &amp; Contact</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
          <DetailRow label="School name" value={inquiry.school_name} />
          <DetailRow label="School email" value={inquiry.email} />
          <DetailRow label="Principal" value={inquiry.principal_name} />
          <DetailRow label="Contact" value={inquiry.contact_number} />
          <DetailRow label="WhatsApp" value={inquiry.whatsapp_number} />
          <DetailRow label="State" value={inquiry.state} />
          <DetailRow label="District" value={inquiry.district} />
          <DetailRow label="City" value={inquiry.city} />
          <DetailRow label="Board" value={boardLabel(inquiry.board)} />
          <DetailRow label="Student strength" value={inquiry.student_strength} />
          <DetailRow label="Existing software" value={inquiry.existing_software} />
          <DetailRow label="Interested product" value={inquiry.interested_product} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Follow-up</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
          <DetailRow label="Inquiry date" value={inquiry.inquiry_date} />
          <DetailRow label="Next follow-up" value={inquiry.next_followup_date} />
          <DetailRow label="Source" value={inquiry.source} />
          <div className="sm:col-span-2 md:col-span-3">
            <DetailRow label="Remarks" value={inquiry.remarks} />
          </div>
          {inquiry.full_address && (
            <div className="sm:col-span-2 md:col-span-3">
              <DetailRow label="Full address" value={inquiry.full_address} />
            </div>
          )}
        </CardContent>
      </Card>

      {convertOpen && (
        <CrmConvertJoinDialog
          inquiryId={inquiry.id}
          onClose={() => setConvertOpen(false)}
          onSuccess={() => { setConvertOpen(false); fetchInquiry(); }}
        />
      )}
    </div>
  );
}
